/**
 * ForgotPasswordPage
 * หน้าลืมรหัสผ่าน
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { authApi } from './auth.api'
import { Mail, ArrowLeft, Loader2, CheckCircle } from 'lucide-react'

const schema = z.object({
  email: z.string().min(1, 'Email is required').email('Invalid email'),
})

type FormValues = z.infer<typeof schema>

export function ForgotPasswordPage() {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [isSent, setIsSent] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
  })

  const onSubmit = async (values: FormValues) => {
    setIsLoading(true)
    try {
      await authApi.requestPasswordReset(values)
      setIsSent(true)
    } catch {
      // Still show success to prevent email enumeration
      setIsSent(true)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[#0c2d6b] via-[#1a3f7a] to-[#0f3460] px-4">
      <div className="relative z-10 w-full max-w-md">
        <div className="rounded-2xl border border-white/10 bg-white/[0.08] p-8 shadow-2xl backdrop-blur-xl">
          {/* Logo */}
          <div className="mb-6 text-center">
            <h1 className="text-3xl font-extrabold tracking-tight text-white">
              Go<span className="text-[#60a5fa]">Audit</span>
            </h1>
          </div>

          {isSent ? (
            <div className="space-y-4 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-500/10">
                <CheckCircle className="h-8 w-8 text-green-400" />
              </div>
              <h2 className="text-xl font-bold text-white">{t('auth.resetLinkSent')}</h2>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white"
              >
                <ArrowLeft className="h-4 w-4" />
                {t('auth.backToLogin')}
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-6 text-center">
                <h2 className="text-xl font-bold text-white">{t('auth.forgotPasswordTitle')}</h2>
                <p className="mt-1 text-sm text-white/50">{t('auth.forgotPasswordSubtitle')}</p>
              </div>

              <form
                onSubmit={(e) => {
                  void handleSubmit(onSubmit)(e)
                }}
                className="space-y-5"
              >
                <div className="space-y-2">
                  <label htmlFor="forgot-email" className="text-sm font-medium text-white/80">
                    {t('auth.email')}
                  </label>
                  <div className="relative">
                    <Mail className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-white/40" />
                    <input
                      id="forgot-email"
                      type="email"
                      autoComplete="email"
                      placeholder={t('auth.emailPlaceholder')}
                      className={`focus:ring-primary/30 h-11 w-full rounded-lg border bg-white/5 pr-4 pl-10 text-sm text-white placeholder-white/30 focus:ring-2 focus:outline-none ${
                        errors.email ? 'border-red-500' : 'border-white/20'
                      }`}
                      {...register('email')}
                    />
                  </div>
                  {errors.email && <p className="text-xs text-red-400">{errors.email.message}</p>}
                </div>

                <Button type="submit" className="h-11 w-full" disabled={isLoading}>
                  {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {t('auth.sendResetLink')}
                </Button>
              </form>

              <div className="mt-4 text-center">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-white/80"
                >
                  <ArrowLeft className="h-4 w-4" />
                  {t('auth.backToLogin')}
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
